<template>
  <div
    v-if="loaded"
    id="app"
    :class="classNames">
    <div
      v-if="isAppOffline"
      class="app-offline">
      <icon-bad-connection />
      <span>{{ $t('messages.offline') }}</span>
    </div>
    <div
      v-if="isAlertVisible"
      class="app-alert"
      :class="alert.status">
      <icon-alert v-if="alert.status === 'unhealthy'" />
      <icon-warning v-if="alert.status === 'warning'" />
      <icon-success v-if="alert.status === 'healthy'" />
      <div class="message">
        <span v-sanitize-html="alert.message" />
      </div>
      <button @click="closeAlert">
        <icon-close />
      </button>
    </div>
    <div
      v-if="isNewVersionAvailable && shouldWarnUserAboutLatestVersion"
      class="app-alert healthy">
      <icon-megaphone />
      <div class="message">
        <span>{{ $t('messages.newVersionAvailable') }}</span>
        <button
          class="btn btn-sm"
          @click="refreshPage">
          {{ $t('actions.refresh') }}
        </button>
      </div>
      <button @click="dismissNewVersion">
        <icon-close />
      </button>
    </div>
    <ax-header
      v-if="!hideHeaderAndNavigation && authenticated && hasChosenMembership && !isPublicRoute"
      :login="login"
      :logout="logout" />
    <ax-header
      v-else-if="isPublicHeader"
      :is-public="true" />
    <notifications />
    <BackgroundOperations />
    <div class="nav-and-content">
      <ax-navbar
        v-if="!hideHeaderAndNavigation && authenticated && hasChosenMembership"
        @toggled="onNavBarToggle" />
      <div
        id="main-content"
        class="main-content"
        :class="isExpanded ? 'expanded' : ''">
        <div
          v-if="didSomethingGoWrong"
          class="flex-v">
          <something-went-wrong />
        </div>
        <RouterView v-else />
      </div>
    </div>
    <modal
      id="blocked_by_client_modal"
      :visible="!isAppOffline && isRequestBlockedByClientWarningVisible"
      :title="capitalize($t('messages.requestBlockedByClient'))"
      :body="capitalize($t('messages.requestBlockedByClientDescription'))"
      :button1-caption="capitalize($t('actions.ok'))"
      @confirmed="isRequestBlockedByClientWarningVisible = false">
      <template #modal-before-text>
        <icon-complex-forbidden />
      </template>
    </modal>
    <Announcements :announcements="announcements" />
    <div id="table_cell_editor" />
    <div
      v-if="lastError !== null"
      id="last_error"
      style="display: none">
      {{ lastError }}
    </div>
    <ax-loader v-if="isLoading" />
    <div
      id="colors"
      :v-show="false">
      <div id="primary-color" />
      <div id="primary-color-light" />
      <div id="primary-color-reverse" />
      <div id="secondary-color" />
      <div id="secondary-color-reverse" />
      <div id="actions-color" />
      <div id="info-color" />
      <div id="success-color" />
      <div id="warning-color" />
      <div id="error-color" />
      <div id="geofences-color" />
    </div>
    <input
      type="hidden"
      name="version"
      :value="version.number" />
  </div>
  <vue-query-devtools v-if="isDev" />
</template>

<script setup lang="ts">
  // Disabling import sort because it breaks the app
  // eslint-disable-next-line simple-import-sort/imports
  import '@/bootstrap.min.css';

  import 'mapbox-gl/dist/mapbox-gl.css';
  import 'vue-multiselect/dist/vue-multiselect.css';
  import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
  import '@vuepic/vue-datepicker/dist/main.css';
  import * as Sentry from '@sentry/vue';
  import { DateTime } from 'luxon';
  import axios from 'axios';
  import LogRocket from 'logrocket';
  import {
    capitalize,
    computed,
    defineAsyncComponent,
    inject,
    onBeforeMount,
    onBeforeUnmount,
    onMounted,
    provide,
    Ref,
    ref,
    watch,
  } from 'vue';

  import LocalCache from '@/utils/LocalCache';
  import SessionCache from '@/SessionCache';
  import { Membership, Organization as OrganizationModel } from '@/common/models';
  import md5 from 'md5';
  import ExchAPIProxy from '@/exch-app/api';
  import GlobalEvents from '@/GlobalEvents';

  import Environment from '@/common/enums/environment.enum';
  import UserRoles from '@/common/enums/user-roles.enum';
  import MessageCollection from '@/validator/MessageCollection';
  import { isPublicRoute as isPublicRouteFromRouter } from '@/router';
  import {
    IconAlert,
    IconBadConnection,
    IconClose,
    IconMegaphone,
    IconSuccess,
    IconWarning,
  } from '@/common/icons/monochrome';
  import { IconComplexForbidden } from '@/common/icons/complex';
  import i18n, { numberFormats } from '@/vueI18n';
  import { DefaultUserProfilePicture, environment } from '@/globals';
  import GlobalEventBus from '@/GlobalEventBus';
  import Organization from '@/common/models/memberships/Organization';
  import Exchange from '@/common/models/memberships/Exchange';
  import QueryParams from '@/common/models/query-params/QueryParams';
  import User from '@/common/models/user/User';
  import version from '@/version.json';
  import OrgAPIProxy, { loginEndpoint, resetPasswordEndpoint, verifyEmailEndpoint } from '@/org-app/api';
  import Announcements from '@/org-app/components/announcements/Announcements.vue';
  import NumbroUtils from './utils/NumbroUtils';
  import { clearCachedQueries } from '@/org-app/services/tanstack/queryUtils';
  import { VueQueryDevtools } from '@tanstack/vue-query-devtools';
  import { decodeToken } from '@/utils/JWTUtils';
  import {
    mapActions,
    mapExchAppActions,
    mapGetters,
    mapOrgAppActions,
    mapOrgAppState,
    mapState,
  } from '@/utils/StateMapping';
  import Validator from '@/validator/Validator';
  import { useRouter, RouterView } from 'vue-router';

  const Notifications = defineAsyncComponent(() => import('@/common/components/Notifications.vue'));
  const AxHeader = defineAsyncComponent(() => import('@/common/components/Header.vue'));
  const AxNavbar = defineAsyncComponent(() => import('@/common/components/NavBar.vue'));
  const AxLoader = defineAsyncComponent(() => import('@/common/components/Loader.vue'));
  const BackgroundOperations = defineAsyncComponent(() => import('@/common/components/BackgroundOperations.vue'));
  const SomethingWentWrong = defineAsyncComponent(() => import('@/common/components/SomethingWentWrong.vue'));

  provide('errors', () => $validator.messageBag.errors || new MessageCollection());
  provide('warnings', () => $validator.messageBag.warnings || new MessageCollection());
  provide('successMessages', () => $validator.messageBag.success || new MessageCollection());
  provide('messageBag', () => $validator.messageBag || new MessageCollection());

  const router = useRouter();

  const $validator: Validator = inject('validator');

  const isNavbarCollapsed = LocalCache.get('isNavBarCollapsed');
  let checkForNewVersionTimer: number | null = null;
  let checkForAlertsTimer: number | null = null;
  let checkForServerDownTimer: number | null = null;
  let checkForAnnoucementsTimer: number | null = null;
  let loadedTimer: number | null = null;

  const alert = ref(null);
  const announcements = ref([]);
  const currentVersionHash = ref(null);
  const didSomethingGoWrong = ref(false);
  const debug = ref('');
  const isLoaderEnabled = ref(true);
  const isLoading = ref(false);
  const isNewVersionAvailable = ref(false);
  const isRequestBlockedByClientWarningVisible = ref(false);
  const latestVersion: Ref<string> = ref(null);
  const loaded = ref(false);
  const loadingIntervalId = ref(-1);
  const lastError = ref(null);
  const isExpanded = ref(
    isNavbarCollapsed === null || isNavbarCollapsed === undefined ? false : isNavbarCollapsed === 'true',
  );
  const shouldWarnUserAboutLatestVersion = ref(false);

  const {
    authenticated,
    isPublicRoute,
    user,
    userRole,
    hasChosenMembership,
    hasChosenExchange,
    hasChosenOrganization,
    permissions,
    isPlannerReserveCampaignIDRole,
  } = mapGetters();

  const { isAppOffline, membership, backgroundOperations } = mapState();

  const { organization } = mapOrgAppState();

  const isDev = computed(() => environment === Environment.DEV);

  const isSalesPlanner = computed(() => (userRole.value ? userRole.value.code === UserRoles.SALES_PLANNER : false));

  const isAlertVisible = computed(() => {
    return alert.value != null && alert.value.message != null && alert.value.message.trim() !== '';
  });

  const classNames = computed(() => {
    let classNames = '';
    if (
      LocalCache.get('shouldBlurMoneyAmounts') != null &&
      LocalCache.get('shouldBlurMoneyAmounts') !== 'false' &&
      LocalCache.get('shouldBlurMoneyAmounts') !== false
    ) {
      classNames += 'blur-money';
    }
    return classNames;
  });

  const hideHeaderAndNavigation = computed(() => {
    return window.location.href.includes('/heat/') || window.location.href.includes('/oops');
  });

  const isPublicHeader = computed(() => {
    return window.location.href.includes('/proposal') || window.location.href.includes('/monitoring');
  });

  const {
    chooseOrganizationType,
    clearMembershipType,
    disableDebugMode,
    getMembership,
    markAsAuthenticated,
    markAsLoggedOut,
    setChosenMembershipType,
    updateAppOffline,
    updateIsNewVersionAvailable,
    updateMembership,
    updateToken,
    updateFreshdeskToken,
    updateUser,
    updateUserRole,
    updateUserProfile,
    storeUserProfile,
  } = mapActions();

  const {
    getExchangeSettings,
    setExchange,
    getPermissions: getExchPermissions,
    clearPermissions: clearExchPermissions,
  } = mapExchAppActions();

  const {
    clearReferenceData,
    getDashboardAlert,
    getLoginAlert,
    getOrganizationSettings,
    getUserProfile,
    getUnreadAnnouncements,
    updateOrganization,
    getPermissions: getOrgPermissions,
    clearPermissions: clearOrgPermissions,
  } = mapOrgAppActions();

  function checkAuth() {
    const token = LocalCache.get('token');
    const refreshToken = LocalCache.get('refreshToken');
    if (!token) {
      return false;
    }

    const decoded = decodeToken(token);
    if (decoded == null) {
      return false;
    }
    const now = Date.now() / 1000.0;

    return refreshToken ? true : now < decoded.exp;
  }

  function login() {}

  function logout() {
    router.push('/login');
    router.isReady().then(() => {
      markAsLoggedOut();
      clearMembershipType();
      updateOrganization(null);
      updateMembership(null);
      setExchange(null);
      updateUser(null);
      updateUserRole(null);
      updateUserProfile(null);
      updateToken(null);
      updateFreshdeskToken(null);
      clearExchPermissions();
      clearOrgPermissions();
      disableDebugMode();
      clearCachedQueries();
    }); // Never catch after this router.push, it will break end to end tests.
    SessionCache.originalRoute = '/login';
  }

  function clearIdentity() {
    updateToken(null);
    markAsLoggedOut();
    clearMembershipType();
    updateOrganization(null);
    updateMembership(null);
    setExchange(null);
    updateUser(null);
    updateUserRole(null);
    updateUserProfile(null);
    clearExchPermissions();
    clearOrgPermissions();
    disableDebugMode();
  }

  function onNavBarToggle(isCollapsed) {
    isExpanded.value = isCollapsed;
  }

  function closeAlert() {
    LocalCache.set('last-dismissed-alert', alert.value);
    alert.value = null;
  }

  function checkForAlerts() {
    const lastDismissedAlert = LocalCache.get('last-dismissed-alert');
    if (authenticated.value) {
      try {
        getDashboardAlert(QueryParams.all()).then(
          (dashboardAlert) => {
            if (
              lastDismissedAlert === undefined ||
              JSON.stringify(dashboardAlert) !== JSON.stringify(lastDismissedAlert)
            ) {
              alert.value = dashboardAlert;
            }
          },
          () => {
            // Do nothing
          },
        );
      } catch {}
    } else {
      try {
        getLoginAlert(QueryParams.all()).then(
          (loginAlert) => {
            if (lastDismissedAlert === undefined || JSON.stringify(loginAlert) !== JSON.stringify(lastDismissedAlert)) {
              alert.value = loginAlert;
            }
          },
          () => {
            // Do nothing
          },
        );
      } catch {}
    }
  }

  function checkForServerDown() {
    try {
      axios.get('/status.json').then((response) => {
        if (response.data?.areWeDown === true) {
          clearIdentity();
          if (!window.location.href.includes('oops')) {
            window.location.href = '/oops';
          }
        } else if (response.data?.areWeDown === false && window.location.href.includes('oops')) {
          window.location.href = '/';
        }
      });
    } catch (e) {
      console.log(e);
    }
  }

  function checkForNewVersion() {
    if (window.environment === 'dev') {
      return;
    }
    try {
      axios.get('/latest-version.json').then((response) => {
        latestVersion.value = response.data.number;
        shouldWarnUserAboutLatestVersion.value = false;

        if (
          latestVersion.value !== currentVersionHash.value &&
          (LocalCache.get('lastDismissedVersion') == null ||
            latestVersion.value !== LocalCache.get('lastDismissedVersion'))
        ) {
          updateIsNewVersionAvailable(true);
          isNewVersionAvailable.value = true;
        }
      });
    } catch (e) {
      console.log(e);
    }
  }

  function goToDefaultOrgRoute() {
    loaded.value = true;
    if (router.currentRoute.value.path === '/login') {
      if (permissions.value.canReadOrganizationperformance) {
        if (organization?.isDemandOrganization) {
          router.push('/buyerdashboard').catch(() => {
            // Do nothing
          });
        } else if (organization?.isSupplyOrganization) {
          router.push('/mediaownerdashboard').catch(() => {
            // Do nothing
          });
        }
      } else if (isSalesPlanner.value || isPlannerReserveCampaignIDRole.value) {
        router.push('/plans').catch(() => {
          // Do nothing
        });
      } else {
        // Sales managers don't have access to organization dashboard.
        // We route them to Line Items screen
        router.push('/lineitems').catch(() => {
          // Do nothing
        });
      }
    }
  }

  function goToDefaultExchangeRoute() {
    loaded.value = true;
    if (router.currentRoute.value.path === '/login') {
      router.push('/exch/deals').catch(() => {
        // Do nothing
      });
    }
  }

  function dismissNewVersion() {
    LocalCache.set('lastDismissedVersion', latestVersion.value);
    isNewVersionAvailable.value = false;
  }

  function refreshPage() {
    checkAuth();
    window.location.reload();
  }

  async function setupMembershipAndOrg() {
    const orgID = new URLSearchParams(document.location.search).get('orgID');
    const orgFromCache = LocalCache.get('organization');
    const membershipFromCache = LocalCache.get('membership');

    //
    // The following block will try to switch organization automatically if the url
    // contains an orgID that does not match the currently logged in organization.
    //
    if (
      authenticated.value &&
      orgID != null &&
      (orgFromCache == null || orgID !== md5(orgFromCache?.organization_id))
    ) {
      const memberships = new Membership(
        membershipFromCache.curator,
        membershipFromCache.exchanges,
        membershipFromCache.dsp,
        membershipFromCache.ssp,
        membershipFromCache.dspParent,
        membershipFromCache.sspParent,
      );
      const matchedOrg = memberships.findOrgMd5(orgID);

      setCurrency(matchedOrg);

      if (matchedOrg !== null && matchedOrg.isActive) {
        const matchOrgModel = OrganizationModel.fromObject(matchedOrg);
        clearReferenceData();
        if (matchOrgModel instanceof Organization) {
          setExchange(null);
          await updateOrganization(matchOrgModel);
          chooseOrganizationType();
          OrgAPIProxy.reset();
          ExchAPIProxy.reset();
          Promise.all([getOrganizationSettings(), getOrgPermissions()]).finally(() => {
            loaded.value = true;
          });
          return;
        }
      } else {
        GlobalEventBus.$emit(GlobalEvents.orgAccessDenied);
      }
    }

    const chosenMembershipType = LocalCache.get('chosenMembershipType');
    setChosenMembershipType(chosenMembershipType);

    if (hasChosenOrganization.value) {
      const organization = LocalCache.get('organization');

      if (!organization) {
        loaded.value = true;
        router.push('/organization-selection').catch(() => {
          // Do nothing
        });
        return;
      }

      let storedOrg = Organization.fromApi(organization);
      storedOrg = membership.value.findOrg(storedOrg.id) || storedOrg;

      await updateOrganization(storedOrg);
      Promise.all([getOrganizationSettings(), getOrgPermissions()]).then(() => goToDefaultOrgRoute());
    } else if (hasChosenExchange.value) {
      const exchange = LocalCache.get('exchange');

      if (!exchange) {
        loaded.value = true;
        router.push('/organization-selection').catch(() => {
          // Do nothing
        });
        return;
      }

      let storedExch = Exchange.fromApi(JSON.parse(exchange));
      storedExch = membership.value.findExch(storedExch.id) || storedExch;

      setExchange(storedExch);
      Promise.all([getExchangeSettings(), getExchPermissions()]).then(() => goToDefaultExchangeRoute());
    } else {
      loaded.value = true;
      router.push('/organization-selection').catch(() => {
        // Do nothing
      });
    }
  }

  function setCurrency(matchedOrg) {
    // make sure to set the currency to i18n in case it was set to the default values by main.js
    if (matchedOrg) {
      NumbroUtils.setCurrencySymbol(matchedOrg.currencySymbol);

      Object.keys(numberFormats).forEach((x) => {
        const currencyFormat = numberFormats[x].currency || matchedOrg.currency;
        currencyFormat.currency = matchedOrg.currency;
        i18n.global.mergeNumberFormat(x, { currency: currencyFormat });
      });
    }
  }

  watch(
    () => organization,
    (newValue) => {
      setCurrency(newValue);
    },
  );

  onBeforeMount(async () => {
    checkForServerDownTimer = setInterval(() => {
      if (document.hasFocus()) {
        checkForServerDown();
      }
    }, 30000) as unknown as number;
    checkForServerDown();

    GlobalEventBus.$on(GlobalEvents.debug, (text) => {
      const time = DateTime.local().toLocaleString(DateTime.TIME_24_WITH_SECONDS);
      if (debug.value !== '') {
        debug.value = `${debug.value}\n${time} :${text}`;
      } else {
        debug.value = `${time}: ${text}`;
      }
    });

    if (checkAuth()) {
      markAsAuthenticated();
    } else if (!isPublicRoute.value) {
      logout();
    }

    if (isPublicRouteFromRouter(window.location.href) || !authenticated.value) {
      loaded.value = true;
      return;
    }

    const userProfile = LocalCache.get('profile');
    if (userProfile) {
      if (userProfile.profile_picture_url == null) {
        userProfile.profile_picture_url = DefaultUserProfilePicture;
      }
      updateUser(User.fromApi(userProfile));
    }

    Sentry.getCurrentScope().setUser({ email: user.value.email });
    //
    // LogRocket config
    //
    if (environment === Environment.STAGING || environment === Environment.UAT) {
      LogRocket.init('ucyhfr/hivestack-app', {
        network: {
          requestSanitizer: (request) => {
            // if the url contains 'ignore'
            if (
              request.url.toLowerCase().includes(loginEndpoint) ||
              request.url.toLowerCase().includes(resetPasswordEndpoint) ||
              request.url.toLowerCase().includes(verifyEmailEndpoint)
            ) {
              // ignore the request response pair
              return null;
            }

            // otherwise log the request normally
            return request;
          },
        },
      });

      LogRocket.identify(user.value.email, {
        email: user.value.email,
      });

      LogRocket.getSessionURL((sessionURL) => {
        Sentry.getCurrentScope().setExtra('sessionURL', sessionURL);
      });
    }

    // fetch and update membership and org
    const membership = LocalCache.get('membership');

    if (!membership) {
      loaded.value = true;
      logout();
      return;
    }

    try {
      const userMembership = await getMembership();
      updateMembership(userMembership);
      const userProfile = await getUserProfile();
      await storeUserProfile(userProfile);
      await setupMembershipAndOrg();
    } catch {
      clearIdentity();
      refreshPage();
    }
  });

  onMounted(() => {
    console.log(`🎉 Version ${version.number}`);

    //
    // Loading events
    //
    GlobalEventBus.$on(GlobalEvents.loading, () => {
      if (!isLoaderEnabled.value) {
        return;
      }

      //
      // Prevent flashing of loader when multiple loading events occur sequentially.
      //
      if (loadedTimer != null) {
        clearTimeout(loadedTimer);
        loadedTimer = null;
      }

      //
      // Ensure that a minimum 200ms delay between loading and loaded event.
      // Prevents flashing for very short load times.
      //
      if (loadingIntervalId.value !== -1) {
        clearInterval(loadingIntervalId.value);
      }
      loadingIntervalId.value = setTimeout(() => {
        isLoading.value = true;
      }, 200) as unknown as number;
    });
    GlobalEventBus.$on(GlobalEvents.loaded, () => {
      if (!isLoaderEnabled.value) {
        return;
      }

      if (loadingIntervalId.value !== -1) {
        clearInterval(loadingIntervalId.value);
      }

      loadedTimer = setTimeout(() => {
        loadedTimer = null;
        isLoading.value = false;
      }, 50) as unknown as number;
    });
    GlobalEventBus.$on(GlobalEvents.logout, () => {
      logout();
    });
    GlobalEventBus.$on(GlobalEvents.logoutDueToPasswordExpiry, () => {
      clearIdentity();
      refreshPage();
    });
    GlobalEventBus.$on(GlobalEvents.unauthorized, () => {
      router.push('/forbidden').catch(() => {
        // Do nothing
      });
    });
    GlobalEventBus.$on(GlobalEvents.orgAccessDenied, () => {
      router.push('/orgAccessDenied').catch(() => {
        // Do nothing
      });
    });
    GlobalEventBus.$on(GlobalEvents.error, (msg) => {
      lastError.value = msg;
    });
    GlobalEventBus.$on(GlobalEvents.disableLoader, () => {
      isLoaderEnabled.value = false;
    });
    GlobalEventBus.$on(GlobalEvents.enableLoader, () => {
      isLoaderEnabled.value = true;
    });
    GlobalEventBus.$on(GlobalEvents.requestBlockedByClient, () => {
      isRequestBlockedByClientWarningVisible.value = true;
    });
    GlobalEventBus.$on(GlobalEvents.somethingWentWrong, () => {
      didSomethingGoWrong.value = true;
    });
    GlobalEventBus.$on(GlobalEvents.clearSomethingWentWrongStatus, () => {
      didSomethingGoWrong.value = false;
    });
    GlobalEventBus.$on(GlobalEvents.resetToken, () => {
      updateToken(null);
      window.location.reload();
    });
    GlobalEventBus.$on(GlobalEvents.clearTooltips, () => {
      const tooltips = window.document.querySelectorAll('.hs-tooltip');
      tooltips.forEach((x) => {
        try {
          x.remove();
        } catch {}
      });
    });

    //
    // Handle requests to fetch unread announcements.
    //
    GlobalEventBus.$on(GlobalEvents.fetchUnreadAnnouncements, async () => {
      // Do not display announcement when running inside Cypress.
      if (window['Cypress']) return;

      if (hasChosenMembership.value) {
        // Note that announcements targeting a supply or demand org are filtered by the API.
        try {
          const announcementsResponse = await getUnreadAnnouncements({ shouldWarnUserOnError: false });
          announcements.value = announcementsResponse ? announcementsResponse.items : [];
        } catch {
          // Do nothing
        }
      }
    });

    GlobalEventBus.$on(GlobalEvents.organizationChanged, () => {
      router.push({ path: '/organization-selection' }).then(() => {
        updateOrganization(null);
        setExchange(null);
        clearMembershipType();
        clearExchPermissions();
        clearOrgPermissions();
        clearCachedQueries();
      });
    });

    //
    // Check for platform alerts every 30 seconds.
    //
    setTimeout(() => {
      checkForAlertsTimer = setInterval(() => {
        if (isPublicRoute.value || !document.hasFocus()) return;
        checkForAlerts();
      }, 30 * 1000) as unknown as number;
      checkForAlerts();
    }, 5000);

    //
    // Check for new announcements every 4 hours, we only display one announcement at a time
    // even when the API sends back multiple ones.
    //
    checkForAnnoucementsTimer = setInterval(
      () => {
        if (isPublicRoute.value) return;
        GlobalEventBus.$emit(GlobalEvents.fetchUnreadAnnouncements);
      },
      4 * 3600 * 1000,
    ) as unknown as number; // 4 hours

    window.addEventListener('keydown', (e) => {
      const evt = (e || window.event) as KeyboardEvent;
      if (evt.code === 'Slash' && evt.ctrlKey && evt.altKey) {
        // CTRL + ALT + /
        const isSampleDataOn = LocalCache.get('isSampleDataOn');
        if (isSampleDataOn != null && isSampleDataOn === 'true') {
          LocalCache.set('isSampleDataOn', 'false');
        } else {
          LocalCache.set('isSampleDataOn', 'true');
        }
        LocalCache.remove('organization');
        router.push('/organization-selection').catch(() => {
          // Do nothing
        });
        window.location.reload();
      } else if (evt.code === 'KeyY' && evt.ctrlKey && evt.altKey) {
        // CTRL + ALT + Y
        const isSampleRevenueSplitDataOn = LocalCache.get('isSampleRevenueSplitDataOn');
        if (isSampleRevenueSplitDataOn != null && isSampleRevenueSplitDataOn === 'true') {
          LocalCache.set('isSampleRevenueSplitDataOn', 'false');
        } else {
          LocalCache.set('isSampleRevenueSplitDataOn', 'true');
          // Create default data in locale storage if none exists.
          if (LocalCache.get('revenueSplit') == null) {
            LocalCache.set(
              'revenueSplit',
              JSON.stringify([
                { name: 'Ad exchange', value: 0.22, color: '#03A9F4' },
                { name: 'Ad server', value: 0.38, color: '#49D6BE' },
                { name: 'Dewzy SSP', value: 0.08, color: '#FFDF02' },
                { name: 'Hivestack SSP', value: 0.12, color: '#FF0083' },
                { name: 'Supply Z SSP', value: 0.09, color: '#5F0A87' },
                { name: 'Voodoo SSP', value: 0.11, color: '#0C0F0A' },
              ]),
            );
          }
        }
        window.location.reload();
      }
    });

    window.addEventListener('online', () => {
      updateAppOffline(false);
    });

    window.addEventListener('offline', () => {
      updateAppOffline(true);
    });

    window.addEventListener('beforeunload', (e) => {
      if (backgroundOperations.value.length > 0) {
        e.returnValue = i18n.global.t('messages.warnAboutUncompletedBackgroundOperations');
      }
    });

    if (window.environment !== 'dev') {
      const appScriptFileUrl = [...window.document.querySelectorAll('script')].find(
        (x) => x.src.indexOf('assets') > -1 && x.src.indexOf('index') > -1,
      ).src;
      [currentVersionHash.value] = appScriptFileUrl.split('/').slice(-2);

      checkForNewVersionTimer = setInterval(checkForNewVersion, 300000) as unknown as number;
      checkForNewVersion();
    }
  });

  onBeforeUnmount(() => {
    if (checkForNewVersionTimer != null) {
      clearInterval(checkForNewVersionTimer);
    }
    if (checkForAlertsTimer != null) {
      clearInterval(checkForAlertsTimer);
    }
    if (checkForServerDownTimer != null) {
      clearInterval(checkForServerDownTimer);
    }
    if (checkForAnnoucementsTimer != null) {
      clearInterval(checkForAnnoucementsTimer);
    }
    // Kill LocalCache cleanup process to avoid unit test crashing when tearing down app
    LocalCache.cleanupTaskRunner.destroy();
  });
</script>

<style lang="scss" rel="stylesheet/scss">
  @use '@/styles/variables' as *;

  #colors {
    #primary-color {
      background-color: var(--primaryColor);
    }

    #primary-color-light {
      background-color: var(--primaryColorLight);
    }

    #primary-color-reverse {
      background-color: var(--primaryColorReverse);
    }

    #secondary-color {
      background-color: var(--secondaryColor);
    }

    #secondary-color-reverse {
      background-color: var(--secondaryColorReverse);
    }

    #info-color {
      background-color: var(--info);
    }

    #success-color {
      background-color: var(--success);
    }

    #warning-color {
      background-color: var(--warning);
    }

    #error-color {
      background-color: var(--error);
    }

    #actions-color {
      background-color: var(--actionsColor);
    }

    #geofences-color {
      background-color: var(--geofencesColor);
    }
  }

  .app-alert {
    z-index: 1;
    display: flex;
    flex-direction: row;
    color: #fff;
    align-items: center;
    font-weight: 600;
    font-size: 1.2em;
    padding: 1.6rem 2.4rem;
    transform-origin: top;
    animation-name: scaleVertical;
    animation-duration: 0.5s;
    animation-timing-function: cubic-bezier(0.2, 1, 0.2, 1);
    animation-fill-mode: backwards;
    box-shadow: inset 0 0 1rem 0 rgba(0, 0, 0, 0.25);
    border-bottom: solid 0.1rem rgba(0, 0, 0, 0.15);
    flex-shrink: 0;

    &.healthy {
      color: var(--successReverse);
      background-color: var(--successDark);
    }

    &.unhealthy {
      color: #fff;
      background-color: red;
    }

    &.warning {
      color: var(--warningReverseDark);
      background-color: var(--warning);

      svg {
        stroke: var(--warningReverseDark);
      }

      button {
        &:hover {
          svg {
            stroke: #fff;
          }
        }
      }
    }

    &.info {
      color: var(--infoReverseDark);
      background-color: var(--info);
    }

    svg {
      stroke: #fff;
      width: 2.4rem;
      margin-right: 0.6rem;
      stroke-width: 0.15rem;
      flex-shrink: 0;
    }

    button {
      background: none;
      border: none;
      padding: 0;
      display: flex;
      align-items: center;
      cursor: pointer;

      svg {
        width: 1.6rem;
        height: 1.6rem;
        stroke-width: 0.2rem;
      }
    }

    .message {
      flex-grow: 1;
      padding-left: 0.6rem;
      //text-shadow: 0 0.1rem 0.1rem rgba(0, 0, 0, 0.25);
      display: flex;
      align-items: center;

      a {
        color: #fff !important;
        text-decoration: underline !important;
        font-weight: 600 !important;
      }

      button {
        outline: none;
        margin-left: 1rem;
        margin-right: 1rem;
        background: transparent;
        border: solid 0.1rem #fff;
        border-radius: 1.6rem;

        color: var(--successReverse);

        font-weight: 500;
        font-size: 0.9em;
        padding: 0.3rem 1.6rem 0.3rem;

        &:hover {
          background: #fff;

          color: var(--success);
          background-color: var(--successReverse);
        }
      }
    }
  }

  .app-offline {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background: #00000080;
    color: #fff;
    z-index: 100000;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    font-size: 6.4rem;

    svg {
      fill: #fff;
      stroke: #fff;
      width: 30rem;
      height: 30rem;
      max-width: 50vw;
      max-height: 50vw;
    }

    span {
      padding: 0 10vw;
      text-align: center;
      font-weight: 200;
      font-size: 4rem;
      margin-top: 5rem;
    }
  }

  .nav-and-content {
    display: flex;
    flex-grow: 1;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    z-index: 1;
  }

  #blocked_by_client_modal {
    .modal-body {
      display: flex;
      flex-direction: column;
      align-items: center;

      svg {
        width: 18.4rem;
        margin: auto;
        margin-bottom: 1.6rem;
      }

      p {
        text-align: center;
      }
    }
  }

  #table_cell_editor {
    position: absolute;
    z-index: 9999; // needed to get the table cell editor above the modal
  }

  #freshworks-frame-wrapper {
    display: none;
  }

  #launcher-frame {
    display: none;
  }
</style>
